import React, { useState } from "react";
import axios from "axios";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [orders, setOrders] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const login = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const auth = { username: user, password: pass };
      const [ordersRes, pendingRes] = await Promise.all([
        axios.get(`${API}/admin/orders`, { auth }),
        axios.get(`${API}/admin/pending-checkouts`, { auth }),
      ]);
      setOrders(ordersRes.data);
      setPending(pendingRes.data || []);
    } catch { setErr("Invalid credentials."); }
    finally { setLoading(false); }
  };

  const verifyPayment = async (orderId) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderId}/verify-payment`, {}, { auth: { username: user, password: pass } });
      setOrders((os) => os.map((o) => (o.id === orderId ? { ...o, status: "confirmed" } : o)));
    } catch { setErr("Couldn't verify payment. Try again."); }
  };

  return (
    <>
      <SEO title="Admin" />
      <section className="max-w-5xl mx-auto px-6 py-12">
        <p className="font-mono-cap text-[10px] text-white/40">Internal</p>
        <h1 className="font-serif text-4xl mt-2">Orders.</h1>
        {!orders ? (
          <form onSubmit={login} className="mt-8 max-w-sm space-y-3">
            <input data-testid="admin-username" placeholder="Username" value={user} onChange={(e) => setUser(e.target.value)} className="w-full bg-transparent border border-white/15 px-3 py-3 text-sm focus:outline-none focus:border-white" />
            <input data-testid="admin-password" type="password" placeholder="Password" value={pass} onChange={(e) => setPass(e.target.value)} className="w-full bg-transparent border border-white/15 px-3 py-3 text-sm focus:outline-none focus:border-white" />
            <button data-testid="admin-login" disabled={loading} className="bg-white text-black font-mono-cap text-xs px-6 py-3 hover:bg-white/90 disabled:opacity-50">{loading ? "..." : "Sign In"}</button>
            {err && <p className="text-red-400 text-sm" data-testid="admin-error">{err}</p>}
          </form>
        ) : (
          <div className="mt-8">
            <p className="text-white/60 mb-4 text-sm" data-testid="admin-count">{orders.length} orders</p>
            <div className="border border-white/10 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/5"><tr>
                  {["Order #", "Customer", "Phone", "Items", "Total", "Payment", "UTR", "Status", "Date"].map((h) => <th key={h} className="text-left p-3">{h}</th>)}
                </tr></thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-t border-white/10" data-testid={`admin-order-${o.order_number}`}>
                      <td className="p-3 font-mono-cap">{o.order_number}</td>
                      <td className="p-3">{o.full_name}<br /><span className="text-white/50 text-xs">{o.city}, {o.pincode}</span></td>
                      <td className="p-3">{o.phone}</td>
                      <td className="p-3">{o.items.map((i) => `${i.product_name} ${i.size}×${i.quantity}`).join(", ")}</td>
                      <td className="p-3">₹{o.total}</td>
                      <td className="p-3">{o.payment_method}</td>
                      <td className="p-3 font-mono text-xs" data-testid={`utr-${o.order_number}`}>
                        {o.utr || "—"}
                        {o.payment_method === "UPI" && o.status !== "confirmed" && o.utr && (
                          <button
                            onClick={() => verifyPayment(o.id)}
                            data-testid={`verify-payment-${o.order_number}`}
                            className="block mt-2 bg-[#FF69B4] text-black px-2.5 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold hover:opacity-90"
                          >
                            Payment Verified ✓
                          </button>
                        )}
                      </td>
                      <td className="p-3">
                        {o.status === "confirmed" ? <span className="text-emerald-400" data-testid={`status-${o.order_number}`}>confirmed ✓</span> : o.status}
                      </td>
                      <td className="p-3 text-xs text-white/50">{new Date(o.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                  {orders.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-white/50">No orders yet.</td></tr>}
                </tbody>
              </table>
            </div>

            {/* Abandoned bags */}
            <div className="mt-12">
              <p className="tiny-link text-[#FF69B4]">Recoverable revenue</p>
              <h2 className="font-display text-3xl mt-2 tracking-[0.03em]">
                ABANDONED BAGS<span className="text-[#FF69B4]">.</span>
              </h2>
              <p className="text-white/50 mt-2 text-sm">
                {pending.length} bag(s) waiting. Tap WhatsApp to nudge the customer from your phone with a pre-written 10% off message. An automated recovery email also fires 30 min after the address is entered.
              </p>
              <div className="border border-[#222] overflow-x-auto mt-4">
                <table className="w-full text-sm">
                  <thead className="bg-white/5"><tr>
                    {["Customer", "Phone", "Email", "Items", "Total", "Age", "Nudge"].map((h) => <th key={h} className="text-left p-3">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {pending.map((p) => (
                      <tr key={p.id} className="border-t border-[#222]" data-testid={`pending-${p.id}`}>
                        <td className="p-3">{p.full_name}</td>
                        <td className="p-3">{p.phone}</td>
                        <td className="p-3 text-xs text-white/60">{p.email}</td>
                        <td className="p-3 text-xs">{(p.items || []).map((i) => `${i.product_name} ${i.size}×${i.quantity}`).join(", ")}</td>
                        <td className="p-3">₹{p.total}</td>
                        <td className="p-3 text-xs text-white/50">{new Date(p.created_at).toLocaleString()}</td>
                        <td className="p-3">
                          {p.whatsapp_url ? (
                            <a
                              href={p.whatsapp_url}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`whatsapp-nudge-${p.id}`}
                              className="inline-block bg-[#25D366] text-black px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase font-semibold hover:opacity-90"
                            >
                              WhatsApp →
                            </a>
                          ) : (
                            <span className="text-white/30 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {pending.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-white/50">Nothing pending. Nice.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
};
export default Admin;
